import { browser } from "$app/environment";

import {
  DOCS_SUGGESTIONS,
  type DocsSearchResult,
  isDocsSearchResult,
} from "$lib/command-palette/utils/docs-search";

/**
 * How often this browser's user has picked each palette destination, keyed
 * by href. Feeds the "Suggested" groups on the Settings and Docs tabs. Lives
 * only in localStorage: nothing is sent anywhere, and it doesn't follow the
 * user across devices.
 */
const STORAGE_KEY = "cmdk-frequency";
/** Entries kept; the least-picked are dropped past this. */
const MAX_ENTRIES = 50;

export interface FrequencyEntry {
  count: number;
  /** Set for docs picks, which have no directory to look the href up in. */
  docs?: DocsSearchResult;
}
export type FrequencyMap = Record<string, FrequencyEntry>;

function isEntry(value: unknown): value is FrequencyEntry {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as { count?: unknown; docs?: unknown };
  if (typeof entry.count !== "number" || !(entry.count > 0)) return false;
  return entry.docs === undefined || isDocsSearchResult(entry.docs);
}

export function loadFrequency(): FrequencyMap {
  if (!browser) return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))
      return {};
    return Object.fromEntries(
      Object.entries(parsed).filter((pair): pair is [string, FrequencyEntry] =>
        isEntry(pair[1]),
      ),
    );
  } catch {
    return {};
  }
}

export function recordSelection(href: string, docs?: DocsSearchResult): void {
  if (!browser) return;
  const frequency = loadFrequency();
  const previous = frequency[href];
  frequency[href] = {
    count: (previous?.count ?? 0) + 1,
    ...(docs
      ? {
          docs: {
            slug: docs.slug,
            kind: docs.kind,
            title: docs.title,
            breadcrumbs: docs.breadcrumbs,
          },
        }
      : previous?.docs
        ? { docs: previous.docs }
        : {}),
  };
  const kept = Object.entries(frequency)
    .sort(([, a], [, b]) => b.count - a.count)
    .slice(0, MAX_ENTRIES);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(kept)));
  } catch {
    // Storage full or disabled: suggestions are a nicety, not worth surfacing.
  }
}

/**
 * The items the user has picked before, most-picked first (ties keep the
 * input order), up to `limit`. Items never picked are left out.
 */
export function rankByFrequency<T>(
  items: readonly T[],
  frequency: FrequencyMap,
  key: (item: T) => string,
  limit: number,
): T[] {
  return items
    .map((item, index) => ({
      item,
      index,
      count: frequency[key(item)]?.count ?? 0,
    }))
    .filter(({ count }) => count > 0)
    .sort((a, b) => b.count - a.count || a.index - b.index)
    .slice(0, limit)
    .map(({ item }) => item);
}

/**
 * Docs for the Docs tab's "Suggested" group: the user's most-picked docs,
 * padded with the curated entry points until there are `limit`. Slugs in
 * `exclude` (e.g. already shown under Recents) are skipped.
 */
export function getSuggestedDocs(
  frequency: FrequencyMap,
  exclude: ReadonlySet<string> = new Set(),
  limit = 5,
): DocsSearchResult[] {
  const seen = new Set(exclude);
  const suggested: DocsSearchResult[] = [];
  const frequent = Object.values(frequency)
    .filter((entry): entry is Required<FrequencyEntry> => !!entry.docs)
    .sort((a, b) => b.count - a.count)
    .map((entry) => entry.docs);
  for (const result of [...frequent, ...DOCS_SUGGESTIONS]) {
    if (suggested.length >= limit) break;
    if (seen.has(result.slug)) continue;
    seen.add(result.slug);
    suggested.push(result);
  }
  return suggested;
}
